<template>
  <div class="row">
    <!-- EMAIL MODE -->
    <div v-if="mode === 'email'" class="col-lg-7 offset-lg-2">
      <div class="card p-5">
        <div class="card-body">
          <!-- STAGE 2: Send verification code (only if not sent) -->
          <template v-if="!sent">
            <h4 class="fw-semibold text-center mb-3">{{ 'Send verification code' | translate }}</h4>
            <div class="mb-4 text-center">
              {{ 'The verification code will be sent to your email address used for login' | translate }}.
            </div>
            <div class="text-center">
              <button
                class="btn btn-dark px-4 btn-sm fw-semibold"
                :disabled="sending || resendLocked"
                @click="sendCode"
              >
                <span v-if="sending" class="spinner-border spinner-border-sm"></span>
                {{ resendLabel }}
              </button>
            </div>
          </template>
          <!-- STAGE 3: Enter verification code (EMAIL) -->
          <div v-else class="text-center">
            <h4 class="fw-semibold mb-3">{{ 'Enter verification code' | translate }}</h4>
            <label for="email-code" class="form-label mb-4">
              {{ 'We have just sent a message to your email address' | translate }}
            </label>
            <input
              id="email-code"
              v-model="code"
              type="text"
              class="form-control w-50 mx-auto d-block mb-4 text-center"
              :class="{ 'is-invalid': codeError }"
              pattern="[0-9]*"
              inputmode="numeric"
              maxlength="6"
              autocomplete="one-time-code"
            />
            <button class="btn btn-dark btn-sm px-4 fw-semibold" @click="verifyCode" :disabled="verifying">
              <span v-if="verifying" class="spinner-border spinner-border-sm"></span>
              {{ 'Verify' | translate }}
            </button>
            <div class="mt-4">
              <button type="button" class="btn btn-link px-0 text-muted" @click="resendCode" :disabled="resendLocked">
                <span v-if="resendLocked">{{ $t('my.waitx', { x: resendTimer }) }}</span>
                <span v-else>{{ $t('Resend code') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PHONE MODE -->
    <div v-else-if="mode === 'phone'" class="col-lg-7 offset-lg-2">
      <div class="card p-5">
        <div class="card-body">
          <!-- STAGE 1: Add phone number -->
          <div v-if="!hasPhone">
            <h4 class="fw-semibold text-center mb-3">
              {{ 'Please enter your phone number to verify your account' | translate }}
            </h4>
            <div class="mb-4 text-center">
              {{ 'This number will be used to send one-time login codes' | translate }}.
            </div>
            <div class="mb-4">
              <div class="d-flex justify-content-center align-items-center">
                <input
                  v-model="prefix"
                  type="text"
                  class="form-control me-2 text-end"
                  :class="{ 'is-invalid': phoneError }"
                  style="width: 80px; flex: 0 0 auto"
                  inputmode="numeric"
                  v-mask="'+####'"
                  maxlength="4"
                  autocomplete="tel-country-code"
                />
                <input
                  id="phone"
                  v-model="phone"
                  type="tel"
                  class="form-control"
                  :class="{ 'is-invalid': phoneError }"
                  placeholder="500123456"
                  autocomplete="tel-national"
                  inputmode="numeric"
                  v-mask="'#############'"
                  style="flex: 1 1 auto; max-width: 200px"
                />
              </div>
              <div class="text-center" v-if="phoneError">
                <small class="text-danger">{{ 'validation.required' | translate }}</small>
              </div>
            </div>
            <div class="text-center">
              <button class="btn btn-dark px-4 btn-sm fw-semibold" @click="savePhone" :disabled="savingPhone">
                <span v-if="savingPhone" class="spinner-border spinner-border-sm"></span>
                {{ 'Send verification code' | translate }}
              </button>
            </div>
          </div>
          <!-- STAGE 2: Send verification code (only if phone is already present and not just added) -->
          <div v-else-if="!sent">
            <h4 class="fw-semibold text-center mb-3">{{ 'Send verification code' | translate }}</h4>
            <div v-if="hasPhoneLocal && lastThreeLocal" class="mb-3 text-center">
              {{ 'The verification code will be sent to your phone number' | translate }}
              <strong>*** *** {{ lastThreeLocal }}</strong>
              .
            </div>
            <div class="text-center">
              <button
                class="btn btn-dark px-4 btn-sm fw-semibold"
                :disabled="sending || resendLocked"
                @click="sendCode"
              >
                <span v-if="sending" class="spinner-border spinner-border-sm"></span>
                {{ resendLabel }}
              </button>
            </div>
          </div>
          <!-- STAGE 3: Enter verification code (PHONE) -->
          <div v-else class="text-center">
            <h4 class="fw-semibold mb-3">{{ 'Enter verification code' | translate }}</h4>
            <div class="mb-4">
              <label for="phone-code" class="form-label">
                {{ 'We have just sent a message to the phone number' | translate }}
                <strong>*** *** {{ lastThreeLocal }}</strong>
              </label>
              <input
                id="phone-code"
                v-model="code"
                type="text"
                class="form-control w-50 mx-auto d-block text-center"
                :class="{ 'is-invalid': codeError }"
                pattern="[0-9]*"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
              />
              <div class="text-center" v-if="codeError">
                <small class="text-danger">{{ 'validation.required' | translate }}</small>
              </div>
            </div>

            <button class="btn btn-dark btn-sm px-4 fw-semibold" @click="verifyCode" :disabled="verifying">
              <span v-if="verifying" class="spinner-border spinner-border-sm"></span>
              {{ 'Verify' | translate }}
            </button>
            <div class="mt-4">
              <button type="button" class="btn btn-link px-0 text-muted" @click="resendCode" :disabled="resendLocked">
                <span v-if="resendLocked">{{ $t('my.waitx', { x: resendTimer }) }}</span>
                <span v-else>{{ $t('Resend code') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask'

export default {
  name: 'SessionVerification',
    directives: {
    mask,
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ['email', 'phone'].includes(v),
    },
    hasInitialPhone: {
      type: [String],
      default: false,
    },
    lastThree: {
      type: [String],
      default: null,
    },
    verificationExpiresAt: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      code: '',
      sent: false,
      sending: false,
      verifying: false,
      phone: '',
      savingPhone: false,
      prefix: '+48',
      resendLocked: false,
      resendTimer: 0,
      resendInterval: null,
      hasPhoneLocal: this.hasInitialPhone === '1',
      lastThreeLocal: this.lastThree,
      phoneError: false,
      codeError: false,
    }
  },
  computed: {
    hasPhone() {
      return this.hasPhoneLocal
    },
    resendLabel() {
      if (this.resendLocked && this.resendTimer > 0) {
        return this.$t('my.waitx', { x: this.resendTimer })
      }
      return this.sent ? this.$t('Resend code') : this.$t('Send verification code')
    },
  },
  mounted() {
    this.checkExistingCode()
  },
  methods: {
    checkExistingCode() {
      if (!this.verificationExpiresAt) return

      const expiresAt = parseInt(this.verificationExpiresAt, 10)
      const now = Math.floor(Date.now() / 1000)
      const secondsUntilExpiry = expiresAt - now

      if (secondsUntilExpiry > 0 && secondsUntilExpiry <= 600) {
        this.sent = true

        const secondsSinceSent = 600 - secondsUntilExpiry
        if (secondsSinceSent < 60) {
          const remainingLockTime = 60 - secondsSinceSent
          this.lockResend(Math.ceil(remainingLockTime))
        }
      }
    },
    redirect() {
      setTimeout(() => {
        window.location.href = '/panel'
      }, 200)
    },
    sendCode() {
      if (this.resendLocked) return
      this.sending = true
      axios
        .post('/json/verification/send')
        .then(({ data }) => {
          if (data.success) {
            this.sent = true
            this.lockResend(60)
          } else {
            this.redirect()
          }
        })
        .catch(() => {
          alert(this.$t('Failed to send verification code.'))
        })
        .finally(() => {
          this.sending = false
        })
    },
    lockResend(seconds = 60) {
      this.resendLocked = true
      this.resendTimer = seconds
      this.resendInterval = setInterval(() => {
        this.resendTimer--
        if (this.resendTimer <= 0) {
          this.resendLocked = false
          clearInterval(this.resendInterval)
        }
      }, 1000)
    },
    verifyingDelay() {
      setTimeout(() => {
        this.verifying = false
      }, 3000)
    },
    verifyCode() {
      this.verifying = true
      this.codeError = false
      axios
        .post('/json/verification/verify', { code: this.code })
        .then(() => {
          this.redirect()
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.codeError = true
          } else {
            alert(this.$t('Invalid or expired verification code.'))
          }
        })
        .finally(() => {
          this.verifyingDelay()
        })
    },
    savePhone() {
      if (!this.phone || !this.prefix) {
        this.phoneError = true
        return
      }
      this.phoneError = false
      this.savingPhone = true

      let phone = this.phone.replace(/\D+/g, '')
      let prefix = this.prefix.replace(/\D+/g, '')

      axios
        .post('/json/verification/phone/save', { phone_number: prefix + phone })
        .then(() => {
          this.hasPhoneLocal = true
          this.lastThreeLocal = phone.slice(-3)
          this.sendCode()
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.phoneError = true
          } else {
            alert(this.$t('Failed to save phone number.'))
          }
        })
        .finally(() => {
          this.savingPhone = false
        })
    },
    resendCode() {
      if (this.resendLocked) return
      this.sendCode()
    },
  },
  beforeDestroy() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval)
    }
  },
}
</script>
